
import LoadingOverlay from "./LoadingOverlay.vue";

import Scheduler from "../SchedulerV2.vue";
import { Vue, Component, Prop, Watch, Emit } from "vue-property-decorator";
import Message from "@/data/notificationsMessages";
import ManagerAdvancedFilter from "../scheduler/filters/ManagerAdvancedFilter.vue";
import { defaultErrorNotificationMessage } from "@/data/errorMessages";
import {
  Company,
  Manager,
  Manager_Bool_Exp,
  Manager_Order_By,
  Order_By,
  Project_Activity_Bool_Exp,
  Project_Bool_Exp,
  User,
} from "@/generated/graphql";
import { NOTIFICATION } from "@/entities/Events";
import Roles from "@/entities/Roles";
import RangeChangedPayload from "@/entities/scheduler/RangeChangedPayload";
import ManagerViewProjectItemFields from "@/entities/scheduler/ManagerViewProjectItem";
import CustomTimelineGroup from "@/entities/scheduler/CustomTimelineGroup";
import {
  getEmptyFiltersResultMessage,
  getEmptyScheduleListMessage,
} from "@/helpers/lists/emptyListMessages";

import getFullName from "@/helpers/getFullName";
import getFetchMoreStartDates from "@/helpers/scheduler/getFetchMoreStartDates";
import NewlyCreatedSchedulerItem from "@/entities/scheduler/NewlyCreatedSchedulerItem";
import ProjectDataForModal from "@/entities/modals/ProjectDataForModal";
import GET_MANAGER_PROJECTS from "@/apollo/scheduler/GetManagerProjects.gql";
import moment from "moment";
import { cloneDeep } from "lodash-es";
import schedulerViewFiltersTamplate from "@/data/filtersTemplates/scheduler/schedulerViewFiltersTamplate";
import ActivityDataForModal from "@/entities/modals/ActivityDataForModal";
import ProjectModalNew from "@/components/companyProjects/ProjectModalNew.vue";
import ActivityFormModal from "@/components/projects/activities/ActivityFormModalNew.vue";
import ScheduleInfoIcon from "@/components/icons/ScheduleInfoIcon.vue";
import { hslColorFromName } from "@/utils/hslColorFromName";
import { getActivityPercentageComplete } from "@/utils/getActivityPercentage";
import storage from "@/services/storage";
import { MessageDuration, MessageStatus } from "@/data/notificationsMessages";
import { EVENTS_BORDERS_COLORS, EVENTS_COLORS } from "@/data/schedularColors";
import adjustDates from "@/helpers/adjustTimeForScheduler";
// Add new imports for top filters
import FormGroup from "@/components/form/FormGroupNew.vue";
import UiButtonMessage from "@/components/button/ui-button-message.vue";
import FormDatepicker from "@/components/form/FormDatepickerNew.vue";

interface SchedulerFilters {
  countries: string[];
  states: string[];
  cities: string[];
  rangeStart: Date;
  rangeEnd: Date;
  orderBy: 0 | 1;
  users: string[]
}

@Component({
  components: {
    Scheduler,
    ManagerAdvancedFilter,
    ActivityFormModal,
    ProjectModalNew,
    ScheduleInfoIcon,
    LoadingOverlay,
    // Add new components for top filters
    FormGroup,
    UiButtonMessage,
    FormDatepicker
  },
  apollo: {
    raw_ManagerProjects: {
      query: GET_MANAGER_PROJECTS,
      update: (data) => {
        return {
          managers: data.manager,
          totalContractors: data.total_contractors?.aggregate?.count || 0
        }
      },
      skip: true,
    },
  },
})
export default class ManagerScheduler extends Vue {
  @Prop({ type: Boolean, default: true }) readonly hasData!: boolean;
  @Prop({ type: Boolean, default: true }) readonly isLoading!: boolean;
  @Prop({ type: Array, default: () => [] }) readonly managers!: any[];

  @Prop({ type: Object, required: true }) readonly currentUser!: User;
  @Prop({ type: Object, required: true }) readonly company!: Company;
  @Prop({ type: String, required: true }) readonly companyId!: string;
  @Prop({ type: Date, default: undefined }) readonly axisStart?: Date;
  @Prop({ type: Date, default: undefined }) readonly axisEnd?: Date;
  @Prop({ type: Array, default: () => [] })
  readonly selectedManagersIds!: string[];
  @Prop({ type: Boolean, default: false })
  readonly showAdditionalFilters!: boolean;
  @Prop({ type: Boolean, default: false })
  readonly shouldClearFilters!: boolean;

  // Add new filter properties
  searchName: string = "";
  searchLocation: string = "";
  searchDate: string = "";

  raw_ManagerProjects: { managers: Manager[], totalContractors: number } = { totalContractors: 0, managers: [] };
  rawManagerProjects: Manager[] = [];
  managerProjects: Manager[] = [];
  queryStartTime: Date = moment().startOf("month").subtract(2, "w").toDate();
  queryEndTime: Date = moment().endOf("month").toDate();
  numberOfAppliedFilters = 0;

  modalShown = false;
  modalType = "activity";
  message: string = "You haven't invited any managers yet";

  projectInfoForModal?: ProjectDataForModal | null = null;
  newlyCreatedItemId?: string = undefined;
  additionalFilters: SchedulerFilters = cloneDeep(schedulerViewFiltersTamplate);
  passedStartDate: Date = moment().startOf("month").toDate();
  passedEndDate: Date = moment().endOf("month").toDate();
  isEdittingLocked: boolean = true;
  activityInfoForModal?: ActivityDataForModal | null = null;
  contractorIdForModal?: string = "";

  created() {
    this.updateTotalCount(0)
    this.$apollo.queries.raw_ManagerProjects.setVariables({
      where: this.where,
      projectsWhere: this.projectsWhere,
      projectActivitiesWhere: this.projectActivitiesWhere
    });
    this.$apollo.queries.raw_ManagerProjects.skip = false;
  }

  // Add watcher for search filters
  get searchFiltersToWatch(): string {
    return JSON.stringify([
      this.searchName,
      this.searchLocation,
      this.searchDate
    ]);
  }

  @Watch("searchFiltersToWatch")
  handleSearchFiltersChange() {
    this.managerProjects = [];
    this.$apollo.queries.raw_ManagerProjects.refetch({
      where: this.where,
      projectsWhere: this.projectsWhere,
      projectActivitiesWhere: this.projectActivitiesWhere,
      orderBy: this.orderBy,
    });
  }

  // Handle reset search button click
  handleResetSearch() {
    this.searchName = "";
    this.searchLocation = "";
    this.searchDate = "";
    this.handleSearchFiltersChange();
  }

  @Emit('update:updateCount')
  updateTotalCount(totalCount: number) {
    return totalCount;
  }

  @Watch("raw_ManagerProjects", { immediate: true })
  detectRawManagerProjects() {
    if (this.raw_ManagerProjects?.managers?.length) {
      this.rawManagerProjects = this.raw_ManagerProjects?.managers
    }
    this.updateTotalCount(this.raw_ManagerProjects?.totalContractors || 0)
  }

  @Watch("rawManagerProjects", { immediate: true })
  rawContractorComposition() {
    if (this.rawManagerProjects.length == 0) {
      return;
    }
    const contractorsAvatarPromises = this.rawManagerProjects.map(
      async (manager) => {
        if (!manager.user.avatar) return manager;
        try {
          const url = await storage.get(manager.user.avatar);
          return {
            ...manager,
            profileImage: url,
          };
        } catch (e) {
          return manager;
        }
      }
    );
    Promise.all(contractorsAvatarPromises)
      .then((managers) => {
        this.managerProjects = managers.map((manager) => {
          return {
            ...manager,
          };
        });
      })
      .catch((e) => { });
    // from raw contractors, make avatar url
  }

  @Watch("propToWatch")
  refetchOnDataFiltersChange(oldVal: string[], newVal: string[]) {
    if (oldVal !== newVal) {
      this.managerProjects = [];
      this.$apollo.queries.raw_ManagerProjects.refetch({
        where: this.where,
        projectsWhere: this.projectsWhere,
        projectActivitiesWhere: this.projectActivitiesWhere,
        orderBy: this.orderBy,
      });
      if (this.rawManagerProjects.length <= 0) {
        this.message = getEmptyFiltersResultMessage("information");
      }
    }
  }

  @Watch("numberOfAppliedFilters", { immediate: true })
  appliedFiltersChanged() {
    this.$emit("appliedFiltersChanged", this.numberOfAppliedFilters);
  }

  get type() {
    return "Manager";
  }

  get schedularType() {
    return `${this.type}s`;
  }

  get projectActivitiesWhere(): Project_Activity_Bool_Exp {
    return {
      drafted: { _neq: true }
    }
  }

  get resources() {
    const data = this.managerProjects;

    const processData = data.map((manager, i) => {
      let totalTaks = 0;
      const color = EVENTS_COLORS[i % EVENTS_COLORS.length];
      const children = [];
      manager.projects.forEach((project) => {


        const projectResource = {
          id: project.id,
          type: "project",
          title: project.client_name ? project.client_name : 'N/A',
          name:  project.client_name ? project.client_name : 'N/A',
          projectId: project.id,
          group: manager.id,
          noOftasks: project.project_activities.length,
          color: color,
          children: [],
          entityInfo: {
            project: project
          },
          // missingContractor: project.project_activities.length === 0,
        };


        project.project_activities.forEach((activity, index) => {
          totalTaks += 1
          const startDate = activity.start_date ? new Date(activity.start_date) : null;
          // Set end date to be the same as start date since activities last one day
          const endDate = startDate ? new Date(startDate.getTime() + 24 * 60 * 60 * 1000) : null;

          const percentageComplete = getActivityPercentageComplete(activity);
          projectResource.children.push({
            id: activity.id,
            index: index + 1,
            title: activity.start_date ? moment(activity.start_date).format("DD/MM/YYYY") : 'N/A',
            name: activity.start_date ? moment(activity.start_date).format("DD/MM/YYYY") : 'N/A',
            type: "activity",
            start_date: startDate,
            end_date: endDate,
            completed: activity.completed,
            note: activity.note,
            budget: activity.budget,
            contractor: activity.contractor,
            location: activity.location,
            virtual: activity.virtual || false,
            virtual_meeting_link: activity.virtual_meeting_link || null,
            fallback_num: activity.fallback_num || null,
            managerId: manager.id,
            group: manager.id,
            confirmed_hours: activity.confirmed_hours,
            color: color,
            progress: percentageComplete,
          });
        });
        projectResource.entityInfo = {
          ...projectResource.entityInfo
        }
        children.push(projectResource);
      });

      const fullName = manager.user.given_name + " " + manager.user.family_name;
      const projectColor = EVENTS_BORDERS_COLORS[i % EVENTS_BORDERS_COLORS.length];
      return {
        ...manager,
        title: fullName,
        color: color,
        subtitle: manager.user.profile?.job_title || '',
        name: fullName,
        type: "manager",
        imageUrl: (manager as unknown as any).profileImage,
        children,
        noOfTotalTaks: totalTaks,
        borderColor: projectColor,

      };
    });
    return processData;
  }

  get events() {
    const events = this.managerActivityItems;

    const me = events.map((e) => {
      return {
        ...e,
        resourceId: e.id,
        startDate: new Date(e.start),
        endDate: new Date(e.end),
        name: e.content,
      };
    });
    return me;
  }

  get listeners() {
    return {
      cellClick: ({ record, event }) => {
        const expanderClass = 'b-tree-expander'
        const isClassNameString = typeof event.target.className === 'string'
        const isExpander = isClassNameString && event.target?.className?.includes(expanderClass) //The className property on an SVG element has a different definition from the className property of an HTML element.In HTML, className is a string. In SVG it is an SVGAnimatedString object
        // Add a new client when clicking plus icon
        const eventdata = record.eventStore.getById(record.data?.id)

        if (isExpander) return false;

        if (event.target.closest(".b-add-activity")) {
          this.handleCreateBtnClicked(record.data);
        } else if (record.data.type === 'activity') {
          this.handleActivityEventClicked(eventdata.data?.entityInfo);
        }
        else if (record.data.type === 'project') {
          this.handleProjectEventClicked(eventdata.data?.entityInfo);
        }
        return false;
      },
      eventClick: (props) => {
        const { eventRecord } = props;
        //eventRecord.data
        if (eventRecord.data.type === "project") {
          this.handleProjectEventClicked(eventRecord.data?.entityInfo);
        }
        if (eventRecord.data.type === "activity") {
          this.handleActivityEventClicked(eventRecord.data?.entityInfo);
        }
      },
      beforeEventAdd: () => {
        return false;
      },
      beforeEventResize: ({ eventRecord }) => {
        if (eventRecord.data.type === "contractor") {
          return false;
        }
        return true;
      },
      beforeEventResizeFinalize: ({ source, context }) => {
        context.async = true;

        const { startDate: newStartDate, endDate: newEndDate, eventRecord } = context;
        const { entityInfo: { project, activityMaxEndDate,
          activityMinStartDate }, type } = eventRecord.data;
        const sdate = new Date(newStartDate.toISOString()).getTime();
        const edate = new Date(newEndDate.toISOString()).getTime();

        const { id: projectId } = project
        let errorMsg = null;
        if (type === "activity") {

          const parentSource = source.eventStore.getById(projectId);
          const { startDate, endDate, } = parentSource.data;
          const psdate = new Date(new Date(startDate).toISOString()).getTime()
          const pedate = new Date(new Date(endDate).toISOString()).getTime()

          const isTimeGreaterOrLessthanProject = edate > pedate || sdate < psdate

          if (isTimeGreaterOrLessthanProject) {
            errorMsg = "Activity Start and End Dates must be between Project Start and End Dates."
          }

        }
        if (type === "project") {
          if (activityMaxEndDate && activityMinStartDate) {
            const maxActivityEndTime = new Date(new Date(activityMaxEndDate).toISOString()).getTime()
            const minActivityStartTime = new Date(new Date(activityMinStartDate).toISOString()).getTime()
            const isTimeGreaterOrLessthanActivity = edate < maxActivityEndTime || sdate > minActivityStartTime
            if (isTimeGreaterOrLessthanActivity) {
              errorMsg = "Project Start and End Dates must be greater than activities Start and End Dates."
            }
          }
        }
        if (errorMsg) {
          this.$eventBus.emit(NOTIFICATION, {
            status: MessageStatus.ERROR,
            title: errorMsg,
            duration: MessageDuration.DEFAULT,
            // message: `You rejected hours for ${this.selectedRows.length || 0} resources.`,
          });
          return context.finalize(false);
        }
        context.finalize(true);

      },
      eventResizeEnd: ({ source, eventRecord, changed }) => {
        if (eventRecord.data.type === "contractor" || !changed) {
          return false;
        }
        const { id, resourceId, startDate, endDate, entityInfo: { project } } = eventRecord.data;
        const start_date = startDate.toISOString();
        const end_date = endDate.toISOString();
        const newSMilis = new Date(start_date).getTime();
        const newEMilis = new Date(end_date).getTime();
        const { id: projectId } = project
        const parentEventSource = source.eventStore.getById(projectId);
        const activitysource = source.eventStore.getById(resourceId);
        const parentReSource = source.resourceStore.getById(projectId);
        const activityResource = source.resourceStore.getById(resourceId);
        if (eventRecord.data.type === "activity") {
          const entityInfo = { ...parentEventSource.entityInfo }
          entityInfo.activityMaxEndDate = new Date(new Date(entityInfo.activityMaxEndDate).toISOString()).getTime() < newEMilis ? endDate : entityInfo.activityMaxEndDate
          entityInfo.activityMinStartDate = new Date(new Date(entityInfo.activityMinStartDate).toISOString()).getTime() > newSMilis ? startDate : entityInfo.activityMinStartDate
          parentEventSource.entityInfo = entityInfo
          parentReSource.entityInfo = entityInfo
          activitysource.start = startDate
          activitysource.end = endDate
          activityResource.start_date = startDate
          activityResource.end_date = endDate
          this.$emit("updateBasicActivityData", id, { start_date, end_date });
          return true;
        }
        if (eventRecord.data.type === "project") {
          activityResource.start_date = startDate
          activityResource.end_date = endDate
          activitysource.start = startDate
          activitysource.end = endDate
          this.$emit("updateProject", {
            set: {
              start_date,
              end_date,
            },
            projectId: eventRecord.data.entityInfo.project.id,
            slug: eventRecord.data.entityInfo.project.slug,
            addSkills: [],
            deleteSkillsIds: [],
            customSkills: [],
            updatePersonalSkills: [],
          });
          return true;
        }
        return false;
      },
    };
  }

  get propToWatch(): string {
    return JSON.stringify([this.selectedManagersIds, this.additionalFilters]);
  }

  get customPlaceholderMessage(): string {
    if (this.numberOfAppliedFilters && !this.managersForGroups.length) {
      return getEmptyFiltersResultMessage("data");
    }
    return getEmptyScheduleListMessage("managers");
  }

  get shouldBlockEditting(): boolean {
    return this.isEdittingLocked && !Boolean(this.activityInfoForModal?.isNew);
  }

  get loading(): boolean {
    return (
      this.$apollo.queries.raw_ManagerProjects.loading &&
      !this.rawManagerProjects.length
    );
  }

  get isManager(): boolean {
    return this.currentUser.role === Roles.MANAGER;
  }

  get isAdmin(): boolean {
    return this.currentUser.role == Roles.ADMIN;
  }

  get startTime() {
    return this.queryStartTime;
  }

  get projectIds(): string[] {
    return this.managerProjects
      .map((manager) => {
        return manager.projects.map((project) => project.id);
      })
      .flat();
  }

  get projectsWhere(): Project_Bool_Exp {
    // Add date filter if searchDate is set
    if (this.searchDate) {
      const formattedDate = moment(this.searchDate).format('YYYY-MM-DD');
      return {
        _and: [
          { project_activities: {} },
          // {
          //   _or: [
          //     // Project start date matches
          //     { start_date: { _gte: formattedDate, _lt: moment(formattedDate).add(1, 'day').format('YYYY-MM-DD') } },
          //     // Project end date matches
          //     { end_date: { _gte: formattedDate, _lt: moment(formattedDate).add(1, 'day').format('YYYY-MM-DD') } },
          //     // Date is between project start and end
          //     {
          //       _and: [
          //         { start_date: { _lte: formattedDate } },
          //         { end_date: { _gte: formattedDate } }
          //       ]
          //     }
          //   ]
          // }
        ]
      };
    }

    // Default query without date filter
    return {
      _and: [
        { project_activities: {} },
        // {
        //   _or: [
        //     {
        //       start_date: {
        //         _lte: this.queryEndTime,
        //         _gte: this.queryStartTime,
        //       },
        //     },
        //     {
        //       end_date: {
        //         _lte: this.queryEndTime,
        //         _gte: this.queryStartTime,
        //       },
        //     },
        //     {
        //       _and: [
        //         {
        //           start_date: {
        //             _lte: this.queryStartTime,
        //           },
        //         },
        //         {
        //           end_date: {
        //             _gte: this.queryEndTime,
        //           },
        //         },
        //       ],
        //     },
        //   ]
        // }
      ]
    };
  }

  get orderBy(): Manager_Order_By {
    return;
    {
      projects_aggregate: {
        count: Order_By.Asc;
      }
    }
  }

  get where(): Manager_Bool_Exp {
    const filterConditions = [];
    const { countries, states, cities, users } = this.additionalFilters;

    if (this.selectedManagersIds.length) {
      filterConditions.push({
        id: {
          _in: this.selectedManagersIds,
        },
      });
    }

    // Add manager name search filter
    if (this.searchName && this.searchName.trim()) {
      filterConditions.push({
        _or: [
          {
            user: {
              _or: [
                { given_name: { _ilike: `%${this.searchName}%` } },
                { family_name: { _ilike: `%${this.searchName}%` } },
                { email: { _ilike: `%${this.searchName}%` } }
              ]
            }
          }
        ]
      });
    }

    // Add location/address search filter
    if (this.searchLocation && this.searchLocation.trim()) {
      filterConditions.push({
        user: {
          profile: {
            _or: [
              { address: { _ilike: `%${this.searchLocation}%` } },
              { city: { _ilike: `%${this.searchLocation}%` } },
              { state: { _ilike: `%${this.searchLocation}%` } },
              { country: { _ilike: `%${this.searchLocation}%` } },
              { zip_code: { _ilike: `%${this.searchLocation}%` } }
            ]
          }
        }
      });
    }

    if (countries.length) {
      filterConditions.push({
        user: {
          profile: {
            country: { _in: countries },
          },
        },
      });
    }

    if (states.length) {
      filterConditions.push({
        user: {
          profile: {
            state: { _in: states },
          },
        },
      });
    }

    if (users.length) {
      filterConditions.push({
        id: { _in: users }
      });
    }

    if (cities.length) {
      filterConditions.push({
        user: {
          profile: {
            city: { _in: cities },
          },
        },
      });
    }

    return { _and: [...filterConditions, { company_id: { _eq: this.companyId } }] };
  }

  get managersForGroups(): Manager[] {
    if (!this.managerProjects) {
      return [];
    }
    return this.managerProjects;
  }

  get projectItems(): ManagerViewProjectItemFields[] | any {
    if (!this.managerProjects) {
      return [];
    }

    return this.managerProjects
      .map((manager) => {
        const groupId = manager.id;
        return manager.projects
          .map((project) => {
            return {
              id: project?.id || "",
              group: groupId,
              content: project?.name! || "",
              start: project?.start_date || "",
              end: project?.end_date || "",
              type: "project",
              entityInfo: {
                project: project,
              },
            };
          })
          .filter((item) => Boolean(item));
      })
      .flat();
  }

  get managerActivityItems() {
    const projectItems = this.projectItems;
    const projectActivities = projectItems
      .map((projectItem) => {
        const groupId = projectItem.group;
        return projectItem.entityInfo.project.project_activities
          .map((activity) => {
            const [adjustedEnd, adjustedStart] = adjustDates(activity.end_date, activity.start_date);

            return {
              id: activity?.id || "",
              group: groupId,
              content: activity?.title! || "",
              start: adjustedStart,
              end: adjustedEnd,
              color: activity?.color || "",
              completed: activity?.completed || false,
              type: "activity",
              entityInfo: {
                activity: activity as ActivityDataForModal,
                project: projectItem.entityInfo.project as ProjectDataForModal,
                contractorId: projectItem.group,
              },
            };
          })
          .filter((item) => Boolean(item));
      })
      .flat();

    return [...projectItems, ...projectActivities];
  }

  async rangeChanged(e: RangeChangedPayload): Promise<void> {
    this.$emit("rangechanged", e);
    const fetchMoreDates = getFetchMoreStartDates(
      this.queryStartTime,
      this.queryEndTime,
      e
    );
    if (!fetchMoreDates) {
      return;
    }
    const { queryStartTime, queryEndTime, whereDatesConditions } =
      fetchMoreDates;
    this.queryStartTime = queryStartTime;
    this.queryEndTime = queryEndTime;

    try {
      await this.$apollo.queries.raw_ManagerProjects.fetchMore({
        variables: {
          where: this.where,
          projectsWhere: {  },
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          const updatedManagers: Manager[] = fetchMoreResult.manager;
          const res = {
            manager: previousResult.manager.map((manager: Manager) => {
              const correspondingManager: Manager | undefined =
                updatedManagers.find((updatedManager) => {
                  return updatedManager.id === manager.id;
                });
              if (correspondingManager) {
                const newProjects = correspondingManager.projects.filter(
                  (project) => {
                    return !this.projectIds.includes(project.id);
                  }
                );
                return {
                  ...manager,
                  projects: [...manager.projects, ...newProjects],
                  __typename: "manager",
                };
              } else {
                return manager;
              }
            }),
          };
          return res;
        },
      });
    } catch (e) {
      this.$eventBus.emit(
        NOTIFICATION,
        Message.errorMessage(defaultErrorNotificationMessage)
      );
    }
  }

  handleIconClose(): void {
    this.$emit("close");
  }

  handleAppliedFiltersNumberChanged(num: number) {
    this.numberOfAppliedFilters = num;
  }

  handleApplyFilters(filters: SchedulerFilters) {
    this.additionalFilters.countries = filters.countries;
    this.additionalFilters.states = filters.states;
    this.additionalFilters.cities = filters.cities;
    this.additionalFilters.users = filters.users;
    this.passedStartDate = filters.rangeStart;
    this.passedEndDate = filters.rangeEnd;
    this.handleIconClose();
  }

  handleReset(filters: SchedulerFilters) {
    this.$emit("filtersReset");
    this.additionalFilters.countries = filters.countries;
    this.additionalFilters.states = filters.states;
    this.additionalFilters.cities = filters.cities;
    this.additionalFilters.users = filters.users;
    this.passedStartDate = filters.rangeStart;
    this.passedEndDate = filters.rangeEnd;
    this.handleIconClose();
  }

  handleCloseModal(): void {
    if (this.modalShown == false) {
      return;
    }
    if (this.newlyCreatedItemId) {
      this.clearNewlyCreatedItemId();
    }
    this.closeModal();
  }

  async handleCollectionUpdated(): Promise<void> {
    this.clearNewlyCreatedItemId();
    await this.refetchCollection();
    this.closeModal();
  }

  async refetchCollection(): Promise<void> {
    await this.$apollo.queries.raw_ManagerProjects.refetch({
      where: this.where,
      projectsWhere: this.projectsWhere,
      orderBy: this.orderBy,
    });
  }

  clearNewlyCreatedItemId(): void {
    this.newlyCreatedItemId = undefined;
  }

  closeModal(): void {
    this.projectInfoForModal = undefined;
    this.modalShown = false;
    this.isEdittingLocked = true;
  }

  handleCreateBtnClicked(activity) {
    this.activityInfoForModal = null;
    this.projectInfoForModal = activity.entityInfo.project;
    this.contractorIdForModal = activity.group;
    this.modalType = "activity";
    this.modalShown = true;
  }

  handleAddActivityClicked(activity: NewlyCreatedSchedulerItem) {
    this.activityInfoForModal = {
      title: "",
      contractor_id: activity.group,
      end_date: "",
      note: "",
      budget: "",
      confirmed_hours: 0,
      requested_hours: 0,
      isNew: true,
    };
    // this.setCurrentlyFetchedItems();
    const projectId = (activity as any).projectId;
    this.projectInfoForModal = { id: projectId, start_date: "", end_date: "" };
    this.contractorIdForModal = activity.group;
    this.modalShown = true;
    this.modalType = "activity";
  }

  handleActivityEventClicked({ activity, project }: any) {
    this.activityInfoForModal = activity;
    this.projectInfoForModal = project;
    this.modalShown = true;
    this.modalType = "activity";
  }

  handleProjectEventClicked({ activity, project }: any) {
    this.activityInfoForModal = activity;
    this.projectInfoForModal = project;
    this.modalShown = true;
    this.modalType = "project";
  }

  handleEditClick(): void {
    this.isEdittingLocked = !this.isEdittingLocked;
  }
}
